import { useEffect, useRef, useState } from "react";
import { openPdf } from "lib/sitePdf";
import { AccessError } from "lib/siteCustomerApi";
import { Notice } from "./Shared";
export default function PdfPreview({ resource, onClose }) {
  const [attempt, setAttempt] = useState(0),
    [viewer, setViewer] = useState(null),
    [page, setPage] = useState(1),
    [zoom, setZoom] = useState(1),
    [error, setError] = useState(null),
    [text, setText] = useState(""),
    [rendering, setRendering] = useState(true);
  const canvas = useRef(null),
    heading = useRef(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    let handle;
    setViewer(null);
    setError(null);
    setPage(1);
    setText("");
    const fail = (e) => {
      if (!abort.signal.aborted) {
        setError(
          e instanceof AccessError ? e : new AccessError("file_unavailable"),
        );
        setViewer(null);
        setText("");
      }
    };
    openPdf(resource.id, abort.signal, fail)
      .then((value) => {
        handle = value;
        if (abort.signal.aborted) value.close();
        else setViewer(value);
      })
      .catch(fail);
    return () => {
      abort.abort();
      handle?.close();
    };
  }, [resource.id, attempt]);
  useEffect(() => {
    if (!viewer) return;
    let live = true,
      task;
    setRendering(true);
    setText("");
    viewer.document
      .getPage(page)
      .then(async (current) => {
        if (!live || !canvas.current) return;
        const viewport = current.getViewport({ scale: 1.4 * zoom }),
          el = canvas.current;
        el.width = viewport.width;
        el.height = viewport.height;
        task = current.render({ canvasContext: el.getContext("2d"), viewport });
        await task.promise;
        const content = await current.getTextContent();
        if (live) {
          setText(content.items.map((x) => x.str).join(" "));
          setRendering(false);
        }
      })
      .catch((e) => {
        if (live && e.name !== "RenderingCancelledException") {
          setError(new AccessError("file_unavailable"));
          setRendering(false);
        }
      });
    return () => {
      live = false;
      task?.cancel();
    };
  }, [viewer, page, zoom]);
  return (
    <section className="ca-panel ca-preview" aria-label="PDF preview">
      <div className="ca-section-head">
        <div>
          <p className="ca-eyebrow">PDF preview</p>
          <h2 tabIndex={-1} ref={heading}>
            {resource.title}
          </h2>
        </div>
        <button onClick={onClose}>Close preview</button>
      </div>
      <Notice error={error} />
      {error ? (
        <button className="ca-primary" onClick={() => setAttempt((v) => v + 1)}>
          Open a fresh preview
        </button>
      ) : !viewer ? (
        <p role="status">Opening secure preview…</p>
      ) : (
        <>
          <div className="ca-actions">
            <button disabled={page === 1} onClick={() => setPage((v) => v - 1)}>
              Previous page
            </button>
            <label>
              Page
              <select
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
              >
                {Array.from({ length: viewer.document.numPages }, (_, i) => (
                  <option key={i + 1}>{i + 1}</option>
                ))}
              </select>
            </label>
            <span>of {viewer.document.numPages}</span>
            <button
              disabled={page === viewer.document.numPages}
              onClick={() => setPage((v) => v + 1)}
            >
              Next page
            </button>
            <label>
              Zoom
              <select
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              >
                <option value={0.75}>75%</option>
                <option value={1}>100%</option>
                <option value={1.5}>150%</option>
              </select>
            </label>
          </div>
          {rendering && <p role="status">Rendering page…</p>}
          <div
            className="ca-canvas"
            tabIndex={0}
            role="region"
            aria-label="Scrollable PDF page"
          >
            <canvas ref={canvas} aria-label={`PDF page ${page}`} />
          </div>
          <details className="ca-text">
            <summary>Read page text</summary>
            <p>
              {text ||
                "This page has no extractable text. It may contain scanned images."}
            </p>
          </details>
          <p className="ca-muted">
            This preview expires after 10 minutes. Use the library’s Download
            button to save your permitted file.
          </p>
        </>
      )}
    </section>
  );
}
